import React from "react"
import {
  ToolTable,
  ToolTableHeader,
  ToolTableBody,
  ToolTableRow,
  ToolTableHead,
  ToolTableCell,
} from "@/ui/ToolTable"
import { HistoricalIrrByVintageData } from "~/api/quarterly_reports"
import { formatPercent, formatCompactQuarter } from "./tableFormatters"

const formatIrrValue = (value: number | null): string => {
  if (value === null) return "-"
  if (value > 999) return "N/A+"
  if (value < -999) return "N/A-"
  return formatPercent(value)
}

const HistoricalIrrByVintageTable = ({
  data,
}: {
  data: HistoricalIrrByVintageData
}) => {
  const { quarters, rows } = data

  return (
    <ToolTable className="component-ui-tool-table--compact">
      <ToolTableHeader>
        <ToolTableRow>
          <ToolTableHead>Vintage</ToolTableHead>
          {quarters.map((quarter, index) => (
            <ToolTableHead key={index}>
              {formatCompactQuarter(quarter)}
            </ToolTableHead>
          ))}
        </ToolTableRow>
      </ToolTableHeader>
      <ToolTableBody>
        {rows.map((row, rowIndex) => (
          <ToolTableRow
            key={rowIndex}
            variant={row.is_total ? "totals" : "default"}
          >
            <ToolTableCell>{row.vintage}</ToolTableCell>
            {row.values.map((value, colIndex) => (
              <ToolTableCell key={colIndex}>
                {formatIrrValue(value)}
              </ToolTableCell>
            ))}
          </ToolTableRow>
        ))}
      </ToolTableBody>
    </ToolTable>
  )
}

const EmptyTable = () => {
  return (
    <div className="editorjs-empty-tool-table">
      No historical IRR by vintage data available
    </div>
  )
}

const hasValidTableData = (data: any): data is HistoricalIrrByVintageData => {
  return (
    data &&
    typeof data === "object" &&
    Array.isArray(data.quarters) &&
    data.quarters.length > 0 &&
    Array.isArray(data.rows) &&
    data.rows.length > 0
  )
}

export class HistoricalIrrByVintageTableTool {
  api: any
  block: any
  createPortal: any
  removePortal: any
  portal: any
  tableData: HistoricalIrrByVintageData | undefined
  headerInserted: boolean

  constructor(toolConfig: any) {
    this.block = toolConfig.block
    this.api = toolConfig.api
    this.createPortal = toolConfig.config?.createPortal
    this.removePortal = toolConfig.config?.removePortal
    this.tableData = toolConfig.config?.tableData

    this.headerInserted = toolConfig.data?.header_inserted || false

    return this
  }

  static get toolbox() {
    return {
      title: "Historical IRR by Vintage",
      icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="3" x2="9" y2="21"/></svg>',
    }
  }

  render() {
    const rootEl = document.createElement("div")

    const hasData = hasValidTableData(this.tableData)

    // Check if we need to insert a header above this block
    queueMicrotask(() => {
      this.checkAndInsertHeaderIfNeeded()
    })

    this.portal = this.createPortal(
      this.block.id,
      hasData ? (
        <HistoricalIrrByVintageTable data={this.tableData!} />
      ) : (
        <EmptyTable />
      ),
      rootEl,
    )

    return rootEl
  }

  async checkAndInsertHeaderIfNeeded() {
    try {
      // Short-circuit if header has already been inserted
      if (this.headerInserted) {
        return
      }

      // Find this block's index by searching through all blocks
      let currentBlockIndex = -1
      const totalBlocks = this.api.blocks.getBlocksCount()

      for (let i = 0; i < totalBlocks; i++) {
        const block = this.api.blocks.getBlockByIndex(i)
        if (block && block.id === this.block.id) {
          currentBlockIndex = i
          break
        }
      }

      if (currentBlockIndex === -1) {
        console.error(
          "ERROR: Could not find current block, skipping header insertion",
        )
        return
      }

      if (currentBlockIndex > 0) {
        const previousBlock = this.api.blocks.getBlockByIndex(
          currentBlockIndex - 1,
        )

        if (previousBlock) {
          // Await the saved data from the previous block
          const previousBlockData = await previousBlock.save()

          // Check if it's a header block with our specific text and level
          if (
            previousBlockData.tool === "header" &&
            previousBlockData.data.text === "Historical IRR By Vintage" &&
            previousBlockData.data.level === 3
          ) {
            // Header exists - mark as inserted even though we didn't insert it
            this.headerInserted = true
            return
          }
        }
      }

      this.insertHeaderBlock(currentBlockIndex)
    } catch (error) {
      console.error("Error in checkAndInsertHeaderIfNeeded:", error)
    }
  }

  insertHeaderBlock(currentBlockIndex: number) {
    try {
      // Insert a header block above the current table block
      this.api.blocks.insert(
        "header",
        {
          text: "Historical IRR By Vintage",
          level: 3,
        },
        {},
        currentBlockIndex,
        true,
      )

      this.headerInserted = true

      // Move focus back to the table block after insertion
      setTimeout(() => {
        this.api.caret.setToBlock(currentBlockIndex + 1)
      }, 50)
    } catch (error) {
      console.error("Error inserting header block:", error)
    }
  }

  remove() {
    const totalBlocks = this.api.blocks.getBlocksCount()
    for (let i = 0; i < totalBlocks; i++) {
      const block = this.api.blocks.getBlockByIndex(i)
      if (block && block.id === this.block.id) {
        this.api.blocks.delete(i)
        break
      }
    }
  }

  removed() {
    this.removePortal(this.block.id)
  }

  save() {
    return {
      type: "historical_irr_by_vintage_table",
      header_inserted: this.headerInserted,
    }
  }

  static get isReadOnlySupported() {
    return true
  }
}
